import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import qs from 'qs';
import { StatusCodes } from 'http-status-codes';
import { isMobile } from 'react-device-detect';

import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import ApiService from 'services/ApiService/ApiService';
import {
  STREAM_PROVIDERS,
  STREAM_PROTOCOL,
  DEVICE_ERRORS,
} from 'utils/consts';
import Cam2CamStore from 'stores/Cam2CamStore';


const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);
const BROADCAST_URL = `${WEBCHAT_HOST}/broadcast`;
const INIT_STREAMING_ROOM_URL = `${WEBCHAT_HOST}v2/streaming-rooms`;
const RETRY_DELAY = 1000;
const MAX_RETRIES = 10;
const defaultResolution = {
  width: 1280,
  height: 720,
  ratio: '16:9',
};

/* eslint-disable-next-line no-magic-numbers */
export const DEFAULT_ASPECT_RATIO = 16 / 9;

export default (UserStore, ProfileStore, FirebaseStore, previewOnly) => {
  const {
    user: { username, accessHash },
    c2cAudioMuted,
    c2cVideoMuted,
    setC2cViewerStreamStarted,
    updateC2CSettingsInFirebase,
  } = UserStore;

  const { performerDetails, profileName: roomname } = ProfileStore;
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
  const streamingRoomData = useRef();
  const retryTimeout = useRef();
  const retries = useRef(0);
  const isMounted = useRef(false);
  const roomInitialized = useRef(false);
  const serverError = useRef(false);
  const { viewerJinkInstance } = Cam2CamStore;

  const onRightClick = useCallback((e) => {
    e.preventDefault();
  }, []);

  const highestResolution = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('C4_C2C_RESOLUTION'));
    } catch {
      return undefined;
    }
  }, []);

  const stopStreaming = useCallback(async() => {
    setC2cViewerStreamStarted(false);

    try {
      await viewerJinkInstance.stopClientMedia();
    } catch {
      // nothing do to here, moving on
    }

    try {
      await viewerJinkInstance.stopPush();
    } catch {
      // nothing do to here, moving on
    }
  }, [setC2cViewerStreamStarted, viewerJinkInstance]);

  const startLocalMedia = useCallback(async(video, audio) => {
    const { setC2cPreviewStream, setC2cError } = Cam2CamStore;
    setC2cError('');
    let stream = null;
    try {
      await viewerJinkInstance.stopClientMedia();
    } catch {
      // no stream present, moving on
    }

    try {
      stream = await viewerJinkInstance.startClientMedia({
        video,
        audio,
        videoElement: 'c2c-input-preview',
      });
    } catch (e) {
      setC2cError(DEVICE_ERRORS.NOT_READABLE_ERROR);
    }

    setC2cPreviewStream(stream);
  }, [viewerJinkInstance]);

  const prepareVideoDevice = useCallback((videoDeviceId) => {
    let videoConstraints = {};

    if (isMobile) {
      const savedMobileFacingMode = localStorage.getItem('C4_C2C_MOBILE_FACING_MODE');
      videoConstraints = { facingMode: { exact: savedMobileFacingMode ? savedMobileFacingMode : 'user' } };
    } else {
      const savedConstraints = JSON.parse(localStorage.getItem('C4_C2C_RESOLUTION'));
      videoConstraints = savedConstraints ? {
        deviceId: { exact: videoDeviceId },
        height: savedConstraints.height,
        width: savedConstraints.width,
      } : { deviceId: { exact: videoDeviceId } };
    }

    return videoConstraints;
  }, []);

  const startStreaming = useCallback(async() => {
    if (!isMounted.current) {
      return;
    }

    const videoDevice = localStorage.getItem('C4_C2C_VIDEO_DEVICE_ID');
    const audioDevice = localStorage.getItem('C4_C2C_AUDIO_DEVICE_ID');
    const { c2cPreviewStream } = Cam2CamStore;
    if (c2cPreviewStream === null) {
      const video = prepareVideoDevice(videoDevice);
      const audio = { deviceId: { exact: audioDevice } };
      await startLocalMedia(video, audio);
    }

    try {
      await viewerJinkInstance.startPush(streamingRoomData.current);
    } catch {
      if (retries.current < MAX_RETRIES) {
        retries.current += 1;
        clearTimeout(retryTimeout.current);
        retryTimeout.current = setTimeout(() => {
          /* eslint-disable-next-line no-use-before-define */
          initStreaming();
        }, RETRY_DELAY);
      }

      return;
    }

    const details = {
      streamUuid: streamingRoomData.current.id.streamUuid,
      width: highestResolution?.width || defaultResolution.width,
      height: highestResolution?.height || defaultResolution.height,
      roomName: performerDetails.username,
      listInDirectory: false,
      cameraName: videoDevice,
      ...(audioDevice ? { microphoneName: audioDevice } : {}),
      autoWidth: highestResolution?.width || defaultResolution.width,
      autoHeight: highestResolution?.height || defaultResolution.height,
      aspectRatio: highestResolution?.ratio || defaultResolution.ratio,
      qualityFactor: 1,
      streamingProvider: STREAM_PROVIDERS.CEEBLUE,
      useStreamingSaas: true,
      mirrorStream: false,
      broadcastedOnBwr: true,
      cam2cam: true,
      isViewerBroadcast: true,
    };

    const data = {
      username,
      accessHash,
      details: JSON.stringify(details),
      streamUri: streamingRoomData.current.input.uri,
    };
    const config = {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    };

    try {
      await ApiService.post(BROADCAST_URL, config);
    } catch {
      if (retries.current < MAX_RETRIES) {
        retries.current += 1;
        clearTimeout(retryTimeout.current);
        retryTimeout.current = setTimeout(() => {
          /* eslint-disable-next-line no-use-before-define */
          initRoom();
        }, RETRY_DELAY);
      }
    }

    setC2cViewerStreamStarted(true);
    updateC2CSettingsInFirebase(username, accessHash, roomname);
  }, [
    highestResolution?.width,
    highestResolution?.height,
    highestResolution?.ratio,
    performerDetails.username,
    username,
    accessHash,
    setC2cViewerStreamStarted,
    updateC2CSettingsInFirebase,
    roomname,
    // eslint-disable-next-line no-use-before-define
    initStreaming,
    // eslint-disable-next-line no-use-before-define
    initRoom,
    viewerJinkInstance,
    startLocalMedia,
    prepareVideoDevice,
  ]);

  const initStreaming = useCallback(async() => {
    const params = {
      username,
      accessHash,
      protocol: STREAM_PROTOCOL.WEBRTC,
      provider: STREAM_PROVIDERS.CEEBLUE,
      cam2cam: true,
      isViewerBroadcast: true,
    };

    try {
      const result = await ApiService.post(INIT_STREAMING_ROOM_URL, { params });
      streamingRoomData.current = result.data;
      startStreaming();
      serverError.current = false;
    } catch (err) {
      if (err.response.status === StatusCodes.INTERNAL_SERVER_ERROR) {
        serverError.current = true;
      }

      if (retries.current < MAX_RETRIES) {
        retries.current += 1;
        clearTimeout(retryTimeout.current);
        retryTimeout.current = setTimeout(() => {
          initStreaming();
        }, RETRY_DELAY);
      }
    }
  }, [accessHash, startStreaming, username]);

  const initRoom = useCallback(() => {
    if (!serverError.current) {
      if (!isMounted.current || previewOnly || roomInitialized.current) {
        return;
      }
      roomInitialized.current = true;
    }
    initStreaming();
  }, [initStreaming, previewOnly]);

  const calculateVideoSize = useCallback(() => {
    const { videoWidth, videoHeight } = document.querySelector('#c2c-input-preview');
    const videoAspectRatio = videoWidth / videoHeight;
    if (videoAspectRatio !== aspectRatio) {
      setAspectRatio(videoAspectRatio);
    }
  }, [aspectRatio]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (c2cAudioMuted) {
      viewerJinkInstance?.muteAudio?.();
    } else {
      viewerJinkInstance?.unmuteAudio?.();
    }
  }, [c2cAudioMuted, viewerJinkInstance]);

  useEffect(() => {
    if (c2cVideoMuted) {
      viewerJinkInstance?.muteVideo?.();
    } else {
      viewerJinkInstance?.unmuteVideo?.();
    }
  }, [c2cVideoMuted, viewerJinkInstance]);

  useEffect(() => {
    document.querySelector('#c2c-input-preview').addEventListener('playing', calculateVideoSize);
    document.querySelector('#c2c-input-preview').addEventListener('timeupdate', calculateVideoSize);

    return () => {
      document.querySelector('#c2c-input-preview').removeEventListener('playing', calculateVideoSize);
      document.querySelector('#c2c-input-preview').removeEventListener('timeupdate', calculateVideoSize);
    };
  }, [calculateVideoSize]);

  useEffect(() => {
    const jinkReInitDelay = 3000;

    let isStopped = false;
    let timer = null;

    const onOnline = () => {
      if (isStopped) {
        isStopped = false;

        if (timer) {
          clearTimeout(timer);
        }

        timer = setTimeout(() => {
          startLocalMedia();
          startStreaming();
        }, jinkReInitDelay);
      }
    };
    const onOffline = async() => {
      try {
        await viewerJinkInstance.stopClientMedia();
      } catch {
        // nothing do to here, moving on
      }

      try {
        await viewerJinkInstance.stopPush();
      } catch {
        // nothing do to here, moving on
      }
      isStopped = true;
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Cam2CamStore.initializeJinkInstance();
  }, []);

  useEffect(() => () => {
    stopStreaming();
  }, [stopStreaming]);

  useEffect(() => {
    if (!previewOnly && viewerJinkInstance) {
      initRoom();
    }
  }, [initRoom, previewOnly, viewerJinkInstance]);

  return { onRightClick, aspectRatio };
};
